import React, { useMemo } from 'react';
import MaterialTable from 'material-table';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import tableIcons from './components/MaterialTableIcons';

const theme = createTheme({
  typography: { h6: { fontSize: '1.4em' } },
});

const safeNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const numberWithCommas = (value) => {
  return safeNumber(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const normalizeInvoices = (data) => {
  if (!Array.isArray(data)) return [];

  return data.map((row) => ({
    ...row,
    Customer_Name: row?.Customer_Name || '',
    Invoice_ID: row?.Invoice_ID || '',
    Invoice_URL: row?.Invoice_URL || '',
    Date_Issued: row?.Date_Issued || '',
    Due_Date: row?.Due_Date || '',
    currency: row?.currency || '',
    Total_AMT: safeNumber(row?.Total_AMT),
    Current_AMT: safeNumber(row?.Current_AMT),
    pay_amt: safeNumber(row?.pay_amt),
    paid_amt: safeNumber(row?.paid_amt),
    Total_Due_Sum: safeNumber(row?.Total_Due_Sum),
  }));
};

const invoiceLinkStyle = {
  color: '#3f51b5',
  cursor: 'pointer',
  textDecoration: 'none',
  fontWeight: 400,
  background: 'transparent',
  border: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
};

const ARID = ({ data, name, sum, currency, loading }) => {
  const safeData = useMemo(() => normalizeInvoices(data), [data]);

  const openInvoicePdf = (rowData) => {
    const pdfUrl = rowData?.Invoice_URL;
    if (!pdfUrl) {
      alert('No invoice PDF found for this invoice.');
      return;
    }
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  };

  const columns = useMemo(
    () => [
      {
        title: 'Compnay Name',
        field: 'Customer_Name',
        cellStyle: { fontSize: '1em' },
        editable: 'never',
      },
      {
        title: 'Invoice #',
        field: 'Invoice_ID',
        editable: 'never',
        render: (rowData) => (
          <span
            onClick={() => openInvoicePdf(rowData)}
            style={invoiceLinkStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
            }}
          >
            {rowData.Invoice_ID}
          </span>
        ),
        cellStyle: { fontSize: '1em' },
      },
      {
        title: 'Invoice Date',
        field: 'Date_Issued',
        cellStyle: { fontSize: '1em' },
        editable: 'never',
      },
      {
        title: 'Due Date',
        field: 'Due_Date',
        cellStyle: { fontSize: '1em' },
        editable: 'never',
      },
      {
        title: 'Currency',
        field: 'currency',
        cellStyle: { fontSize: '1em', textAlign: 'right' },
        editable: 'never',
      },
      {
        title: 'Invoice Amount',
        field: 'Total_AMT',
        render: (rowData) => (
          <div style={{ textAlign: 'right' }}>
            {numberWithCommas(rowData.Total_AMT)}
          </div>
        ),
        cellStyle: { fontSize: '1em' },
      },
      {
        title: 'Due Amount',
        field: 'Current_AMT',
        render: (rowData) => (
          <div style={{ textAlign: 'right' }}>
            {numberWithCommas(rowData.Current_AMT)}
          </div>
        ),
        cellStyle: { fontSize: '1em' },
      },
      {
        title: 'Paid Amount',
        field: 'paid_amt',
        render: (rowData) => (
          <div style={{ textAlign: 'right' }}>
            {numberWithCommas(rowData.paid_amt)}
          </div>
        ),
        cellStyle: { fontSize: '1em' },
      },
      {
        title: 'Past Amount',
        field: 'pay_amt',
        render: (rowData) => (
          <div style={{ textAlign: 'right' }}>
            {numberWithCommas(rowData.pay_amt)}
          </div>
        ),
        cellStyle: { fontSize: '1em' },
      },
      {
        title: '[Total Amount]',
        field: 'Total_Due_Sum',
        render: (rowData) => (
          <div style={{ textAlign: 'right' }}>
            {numberWithCommas(rowData.Total_Due_Sum)}
          </div>
        ),
        cellStyle: { fontSize: '1em' },
      },
    ],
    []
  );

  const options = useMemo(
    () => ({
      sorting: true,
      search: false,
      searchFieldAlignment: 'left',
      searchAutoFocus: false,
      searchFieldVariant: 'outlined',
      filtering: false,
      paging: true,
      pageSizeOptions: [5, 10, 15, 25, 50],
      pageSize: 5,
      paginationType: 'stepped',
      showFirstLastPageButtons: false,
      paginationPosition: 'bottom',
      exportButton: { csv: true },
      exportAllData: true,
      exportFileName: 'Payment_Summary',
      addRowPosition: 'first',
      actionsColumnIndex: -1,
      selection: false,
      showSelectAllCheckbox: false,
      showTextRowsSelected: false,
      grouping: false,
      columnsButton: true,
      debounceInterval: 500,
      rowStyle: (data, index) => (index % 2 === 0 ? { background: '#f5f5f5' } : null),
      headerStyle: { background: 'lightgray', fontStyle: 'italic', fontSize: 19 },
    }),
    []
  );

  return (
    <div className="ARPaymentID">
      <h1 align="center">Invoice Detail(Receivable)</h1>

      <ThemeProvider theme={theme}>
        <MaterialTable
          title={'Company Name : ' + (name || '')}
          icons={tableIcons}
          columns={columns}
          data={safeData}
          isLoading={!!loading}
          options={options}
        />
      </ThemeProvider>

      <h2>
        Total Amount : {numberWithCommas(sum)} - {currency || ''}
      </h2>
    </div>
  );
};

export default React.memo(ARID);